import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserCog, Ban, Undo2, Trash2, Crown, Shield, Users } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";

const roleLabels = { player: "Joueur", coach: "Entraîneur", admin: "Directeur technique" };

export default function Manage() {
  const { user: me, refreshUser } = useAuth();
  const [players, setPlayers] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const isAdmin = me?.role === "admin";
  const canManage = me?.role === "coach" || isAdmin;

  const load = async () => {
    try {
      setLoading(true);
      const [p, c] = await Promise.all([api.get("/players"), api.get("/players/counts")]);
      setPlayers(p.data);
      setCounts(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const call = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  const changeRole = (p, newRole) =>
    call(`role-${p.id}-${newRole}`, async () => {
      await api.put(`/players/${p.id}/role`, { role: newRole });
      toast.success(`${p.name} → ${roleLabels[newRole]}`);
    });

  const toggleBan = (p) =>
    call(`ban-${p.id}`, async () => {
      const action = p.is_banned ? "unban" : "ban";
      if (action === "ban" && !confirm(`Bannir ${p.name} ? Il ne pourra plus se connecter.`)) {
        throw { response: {} };
      }
      await api.post(`/players/${p.id}/${action}`);
      toast.success(action === "ban" ? "Compte banni" : "Compte débanni");
    });

  const del = (p) =>
    call(`del-${p.id}`, async () => {
      if (!confirm(`Supprimer définitivement ${p.name} et toutes ses stats ?`)) {
        throw { response: {} };
      }
      await api.delete(`/players/${p.id}`);
      toast.success("Joueur supprimé");
    });

  const transferDT = (p) =>
    call(`transfer-${p.id}`, async () => {
      const choice = prompt(
        `Transférer le poste de Directeur technique à ${p.name} ?\nTape "coach" pour te rétrograder en coach, "player" pour te rétrograder en joueur.`,
        "coach"
      );
      if (!choice || !["coach", "player"].includes(choice)) {
        throw { response: {} };
      }
      await api.post(`/players/${p.id}/transfer-admin`, { fallback_role: choice });
      toast.success("Nouveau Directeur technique");
      await refreshUser();
    });

  if (!canManage) {
    return (
      <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-slate-500">
        Accès réservé aux entraîneurs et au directeur technique.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
          Administration
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight flex items-center gap-3">
          <UserCog className="w-10 h-10 text-[#E5B85C]" />
          Gestion des comptes
        </h1>
      </div>

      {counts && (
        <div className="grid grid-cols-3 gap-3">
          <StatBox label="Directeur technique" value={`${counts.admins}/${counts.max_admins}`} color="#E5B85C" />
          <StatBox label="Entraîneurs" value={`${counts.coaches}/${counts.max_coaches}`} color="#60A5FA" />
          <StatBox label="Joueurs" value={counts.players} color="#94A3B8" />
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : (
        <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
          <div className="divide-y divide-white/5">
            {players.map((p) => {
              const isMe = p.id === me?.id;
              const canDelete = !isMe && p.role !== "admin";
              const canBan = !isMe && p.role !== "admin";
              const canChangeRole = isAdmin && !isMe;
              return (
                <div
                  key={p.id}
                  data-testid={`manage-row-${p.id}`}
                  className={`p-4 flex flex-col md:flex-row md:items-center gap-3 ${p.is_banned ? "opacity-60" : ""}`}
                >
                  <Link
                    to={`/players/${p.id}`}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    <Avatar player={p} size={44} />
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate flex items-center gap-2 flex-wrap">
                        {p.name}
                        {isMe && <Badge label="Moi" color="#E5B85C" />}
                        {p.role === "admin" && <Badge label="DT" color="#E5B85C" />}
                        {p.role === "coach" && <Badge label="Entraîneur" color="#60A5FA" />}
                        {p.is_banned && <Badge label="Banni" color="#DC2626" />}
                      </div>
                      <div className="text-xs text-slate-400 truncate">
                        {p.email}
                      </div>
                    </div>
                  </Link>

                  <div className="flex flex-wrap items-center gap-2">
                    {canChangeRole && (
                      <select
                        data-testid={`role-select-${p.id}`}
                        disabled={busy === `role-${p.id}-${p.role}`}
                        value={p.role}
                        onChange={(e) => changeRole(p, e.target.value)}
                        className="px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-xs font-bold uppercase tracking-wide text-white"
                      >
                        <option value="player">Joueur</option>
                        <option
                          value="coach"
                          disabled={counts && counts.coaches >= counts.max_coaches && p.role !== "coach"}
                        >
                          Entraîneur
                        </option>
                        <option
                          value="admin"
                          disabled={counts && counts.admins >= counts.max_admins && p.role !== "admin"}
                        >
                          Directeur technique
                        </option>
                      </select>
                    )}

                    {isAdmin && !isMe && p.role !== "admin" && (
                      <button
                        data-testid={`transfer-${p.id}`}
                        onClick={() => transferDT(p)}
                        disabled={busy === `transfer-${p.id}`}
                        title="Transférer le poste de DT"
                        className="p-2 rounded-lg bg-[#E5B85C]/10 border border-[#E5B85C]/30 text-[#E5B85C] hover:bg-[#E5B85C]/20 disabled:opacity-40"
                      >
                        <Crown className="w-4 h-4" />
                      </button>
                    )}

                    {canBan && (
                      <button
                        data-testid={`ban-${p.id}`}
                        onClick={() => toggleBan(p)}
                        disabled={busy === `ban-${p.id}`}
                        title={p.is_banned ? "Débannir" : "Bannir"}
                        className={`p-2 rounded-lg border disabled:opacity-40 ${
                          p.is_banned
                            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20"
                            : "bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20"
                        }`}
                      >
                        {p.is_banned ? <Undo2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </button>
                    )}

                    {canDelete && (
                      <button
                        data-testid={`delete-${p.id}`}
                        onClick={() => del(p)}
                        disabled={busy === `del-${p.id}`}
                        title="Supprimer"
                        className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 disabled:opacity-40"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-xl bg-slate-900/50 border border-white/5 p-4 text-xs text-slate-400 font-mono-stat leading-relaxed">
        <div className="uppercase tracking-widest text-slate-500 mb-2">Rappel des règles</div>
        <ul className="space-y-1">
          <li>• Max <span className="text-[#E5B85C]">1 Directeur technique</span> (admin principal)</li>
          <li>• Max <span className="text-sky-400">2 entraîneurs</span></li>
          <li>• Le DT ne peut pas être supprimé ou banni — le rétrograder d'abord</li>
          <li>• Ban = compte conservé mais impossible de se reconnecter. Suppression = tout est effacé</li>
        </ul>
      </div>
    </div>
  );
}

function Badge({ label, color }) {
  return (
    <span
      className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-widest font-mono-stat"
      style={{ background: `${color}20`, color }}
    >
      {label}
    </span>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="p-4 rounded-xl bg-slate-900 border border-white/10 relative overflow-hidden">
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: color }} />
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div className="font-display font-black text-3xl mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
